// The document id an admin form mints for a new record.
//
// An id here is not an opaque key: it is the last segment of the public URL
// (`/experiences/uchikake-photo-walk`), it is what a collection's members point
// at, and it is what an old link keeps resolving to. So it is derived from the
// name, shown to whoever is creating the record, and never changed afterwards —
// renaming a record leaves its id alone on purpose.
//
// Uniqueness is a separate, per-collection question, asked of Firestore: see
// `isDocIdTaken` and the `create*` functions in `src/data`.

/**
 * Long enough for the longest real name, short enough to keep a URL readable
 * and well inside Firestore's 1500-byte id limit.
 */
export const maxSlugLength = 60;

/**
 * The id a name suggests: lower case, words joined by single hyphens, anything
 * that is not a-z or 0-9 dropped.
 *
 * A name of nothing but non-Latin characters — Japanese, most usefully — yields
 * an empty string rather than a mangled one. That is a refusal, not a failure:
 * the form asks for an id instead of writing to `doc(coll, '')`, which throws.
 */
export function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  // Truncating can leave the hyphen that used to be between two words as a
  // trailing one, so the trim is done again rather than only before the cut.
  return slug.slice(0, maxSlugLength).replace(/-+$/g, '');
}

/**
 * A slug as it is being typed: the same alphabet as `slugify`, but a trailing
 * hyphen is left alone so that typing one is possible at all.
 *
 * For the id field's `onChange`. `slugify` is what runs on the way to Firestore.
 */
export function normaliseSlugInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, maxSlugLength);
}

/**
 * Why a slug cannot be used, or null when it can — shape only. Whether another
 * record already holds it is a Firestore question, asked separately.
 */
export function validateSlug(slug: string): string | null {
  if (!slug) return 'An id is needed. Type one if the name has no Latin letters.';
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
    return 'Use lower-case letters, numbers and single hyphens between them.';
  }
  if (slug.length > maxSlugLength) return `Keep it to ${maxSlugLength} characters.`;
  return null;
}

/**
 * Why this id cannot be used, or null. Shape first — a malformed one is worth
 * saying without a round trip — then whether the collection already holds it,
 * which only Firestore can answer.
 *
 * The answer has a shelf life: the catalogue can gain the id between this check
 * and the write, which is why the write is a transaction (see `createDocument`)
 * rather than trusting what this returned.
 */
export async function validateSlugForCreate(
  slug: string,
  isTaken: (slug: string) => Promise<boolean>,
  entityLabel: string,
): Promise<string | null> {
  const shapeError = validateSlug(slug);
  if (shapeError) return shapeError;
  try {
    return (await isTaken(slug))
      ? `Another ${entityLabel} already uses “${slug}”. Choose something else.`
      : null;
  } catch (e: unknown) {
    // Better to say the check didn't happen than to create a record over one
    // that might already be there.
    console.error('Id availability check failed', e);
    return 'Could not check whether this id is free. Try again.';
  }
}
